import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { Col, Row } from 'antd'

import AddToCartBtn from '@/components/AddToCartBtn'
import B2becTable from '@/components/B2becTable'
import ProductCard from '@/components/ProductCard'
import ProductComparisonModal from '@/components/ProductComparisonModal'
import { sendProductDetail, showAddProductToWishlistModal } from '@/store/slices/wishlistSlice'
import { useDeviceDetect } from '@/hooks'
import useDummyItems from '@/hooks/useDummyItems'
import usePermission from '@/hooks/usePermission'
import useProductComparison from '@/hooks/useProductComparison'
import { MODULE_SHOP_NAVIGATION_RIGHTS } from '@/libs/constants/modulerights'
import { linkGenerator } from '@/libs/utils/language'

import listColumns from './renderColumns'

const emptyProducts = []

export const GridView = (props) => {
  const { productsList = emptyProducts, onAddProductToCart, dispatch } = props
  const {
    verifiedPermissions: [
      hasViewListPricePermission,
      hasAddProductToCartPermission,
    ],
  } = usePermission([
    MODULE_SHOP_NAVIGATION_RIGHTS.VIEW_LIST_PRICE,
    MODULE_SHOP_NAVIGATION_RIGHTS.ADD_PRODUCT_TO_SHOPPING_CART,
  ])

  const {
    onCompareHandler,
    comparingProductIds,
    showComparisonModal,
    setShowComparisonModal,
    productInformation,
  } = useProductComparison()

  const onCheckHandler = useCallback(
    (product, event) => {
      const isChecked = event?.target?.checked

      onCompareHandler(isChecked, product)
    },
    [onCompareHandler],
  )

  const handleAddToWishlist = ({ materialName, materialNumber, img }) => {
    dispatch(sendProductDetail({ materialName, materialNumber, img }))
    dispatch(showAddProductToWishlistModal())
  }

  return (
    <>
      <Row gutter={20} style={{ marginBottom: 20 }}>
        {productsList.map((item) => (
          <Col
            key={item?.materialNumber}
            className="card-item"
            lg={8}
            md={12}
            sm={24}
            xl={8}
            xs={24}
          >
            <ProductCard
              isCompareCheckboxDisplayed
              category={item.category}
              comparingProductIds={comparingProductIds}
              currencyUnit={item?.currencyUnit || item?.currency}
              img={item?.img || item?.imageUrl}
              listPrice={item?.listPrice || item?.price}
              materialNumber={item?.materialNumber || item?.materialId}
              name={item?.name}
              requiresInstruction={item?.requiresInstruction}
              addToCartBtn={
                <AddToCartBtn
                  handleClick={() => onAddProductToCart(item?.materialNumber || item?.materialId)}
                />
              }
              displayAddToCart={item?.displayAddToCart && hasAddProductToCartPermission}
              shouldDisplayListPrice={item?.displayListPrice && hasViewListPricePermission}
              onAddToWishlist={handleAddToWishlist}
              onCheckHandler={onCheckHandler}
            />
          </Col>
        ))}
      </Row>

      <ProductComparisonModal
        productsData={productInformation}
        setShowModal={setShowComparisonModal}
        showModal={showComparisonModal}
      />
    </>
  )
}

export const ListView = (props) => {
  const {
    dataSrc = emptyProducts,
    isLoading = false,
    onAddProductToCart,
  } = props

  const {
    onCompareHandler,
    comparingProductIds,
    showComparisonModal,
    setShowComparisonModal,
    productInformation,
  } = useProductComparison()

  const {
    verifiedPermissions: [
      hasAddProductToCartPermission,
      hasProductComparisonPermission,
    ],
  } = usePermission([
    MODULE_SHOP_NAVIGATION_RIGHTS.ADD_PRODUCT_TO_SHOPPING_CART,
    MODULE_SHOP_NAVIGATION_RIGHTS.PRODUCT_COMPARISION,
  ])

  const { isIpadLandscape } = useDeviceDetect()
  const navigate = useNavigate()

  const fakeItems = useDummyItems(5, {
    img: '',
    materialNumber: '',
    _name: '',
    listPrice: 0,
  })

  const handleCellClick = (materialNumber) => {
    return navigate(linkGenerator(`/product/${materialNumber}`))
  }

  const onCheckHandler = useCallback(
    (product, event) => {
      const isChecked = event?.target?.checked
      const selectedProduct = {
        materialNumber: product.materialNumber,
        materialName: product.mediaName,
        categoryName: product.category,
        listPrice: product.price,
        currencyUnit: product.currency,
        pictureUrl: product.imageUrl,
      }
      onCompareHandler(isChecked, selectedProduct)
    },
    [onCompareHandler],
  )

  const renderColumns = () => {
    const currentTableColumns = listColumns(
      onAddProductToCart,
      handleCellClick,
      isLoading,
      onCheckHandler,
      comparingProductIds,
      hasAddProductToCartPermission,
      hasProductComparisonPermission,
    )
    // Don't render the column of comaprison feature in mobile view
    return isIpadLandscape
      ? currentTableColumns.filter(
        (column) => column?.dataIndex !== 'comparison',
      )
      : currentTableColumns
  }

  return (
    <>
      <B2becTable
        className="order-details-cart-table"
        columns={renderColumns()}
        dataSource={isLoading ? fakeItems : dataSrc}
        pagination={false}
        rowKey="materialNumber"
        style={{ marginTop: 20 }}
      />
      <ProductComparisonModal
        productsData={productInformation}
        setShowModal={setShowComparisonModal}
        showModal={showComparisonModal}
      />
    </>
  )
}
